import React, { useEffect, useState, useCallback } from 'react';
import { sharingApi, SharedMemberItem } from '../api/sharing';
import { formatApiError } from '../utils/errorUtils';
import MemberCard from '../components/member/MemberCard';
import PageBar from '../components/PageBar';
import './AdminConfig.css';

const IconHome = () => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M3 9.5 12 3l9 6.5V21a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z" />
  </svg>
);

const IconUsers = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
  </svg>
);

/**
 * Virtual "family" view: data only from GET /api/sharing/members (no families row in DB).
 *
 * Migrated to the shared `<PageBar>` chrome that Families / FamilyDetail
 * use. The previous custom `.admin-hero` block was removed; the only
 * piece of meta from the old hero that survives is the people-count,
 * which now lives in a thin toolbar row above the grid.
 */
const SharedMembers: React.FC = () => {
  const [rows, setRows] = useState<SharedMemberItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setRows(await sharingApi.listInboundMembers());
    } catch (err: unknown) {
      const detail =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : undefined;
      setError(formatApiError(detail) || 'Eroare la încărcare');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Rendered identically across loading / empty / populated states so
  // crumbs and the back button never depend on data fetch outcomes.
  const pageBar = (
    <PageBar
      crumbs={[
        { label: 'Dashboard', to: '/dashboard', icon: <IconHome /> },
        { label: 'Membri partajați' },
      ]}
      backTo="/dashboard"
      leadingChip={
        <span
          className="page-bar-crumb-chip"
          style={{ background: 'var(--color-slate-100)', color: 'var(--color-slate-700)' }}
          aria-hidden
        >
          <IconUsers />
        </span>
      }
    />
  );

  if (loading) {
    return (
      <div className="page-bar-page shared-members-page">
        {pageBar}
        <div className="page-container admin-page page-bar-page-content">
          <div className="grid grid-2">
            {[1, 2].map((i) => (
              <div key={i} className="skeleton skeleton-card" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-bar-page shared-members-page">
      {pageBar}

      <div className="page-container admin-page page-bar-page-content">
        {error && <div className="alert alert-error">{error}</div>}

        {rows.length === 0 ? (
          <div className="card">
            <div className="empty-state">
              <svg
                className="empty-state-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              <div className="empty-state-title">Nicio persoană partajată</div>
              <div className="empty-state-text">Când cineva îți partajează un membru, îl vei vedea aici.</div>
            </div>
          </div>
        ) : (
          <>
            <div
              className="flex items-center"
              style={{
                gap: 'var(--sp-2)',
                marginBottom: 'var(--sp-3)',
                color: 'var(--color-slate-600)',
                fontSize: 'var(--text-sm)',
              }}
            >
              <span>
                {rows.length} {rows.length === 1 ? 'persoană' : 'persoane'}
              </span>
            </div>
            <div className="grid grid-2">
              {rows.map((row) => (
                <MemberCard
                  key={row.member_id}
                  showFamily
                  member={{
                    id: row.member_id,
                    name: row.name,
                    family_id: row.family_id,
                    family_name: row.family_name,
                    stats: row.stats,
                  }}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SharedMembers;
